import type { SignupOffer } from '@promotion/types'
import { type UseQueryOptions, type UseQueryOverrideOptions, type PromotionClient, ExperimentCookieName, ExperimentName } from '@core/types'
import { computed, createSharedComposable, useCookie, useDayjs, useExperiment, usePromotionLogger, usePromotionConfig, usePromotionClient, useUserInfo, useRoute } from '#imports'

type SignupPromotionsResponse = PromotionClient.components['schemas']['SignupPromotionsResponse']
type PromotionSignupData = PromotionClient.components['schemas']['PromotionSignupData']

export interface UseSignupBundlesOptions extends Omit<UseQueryOptions<SignupPromotionsResponse>, 'key' | 'enabled' | 'query' | 'service'> {
    experiment?: ExperimentCookieName
}

export const useSignupBundles = createSharedComposable((options: UseSignupBundlesOptions = {}) => {
    const { experiment = ExperimentCookieName.BUNDLES, ...queryOptions } = options

    const route = useRoute()
    const { signupBundleExperiments } = usePromotionConfig()
    const bundlesExperiment = useExperiment({ cookieName: experiment, experimentName: ExperimentName.BUNDLES })
    const { isLoggedIn } = useUserInfo()
    const { useGetSignupOffer } = usePromotionClient()
    const dayjs = useDayjs()

    const signupBonusCode = useCookie('signup-bonus:code', {
        sameSite: 'lax',
        maxAge: dayjs.duration({ days: 7 }).asSeconds(),
        path: '/',
        secure: true,
        httpOnly: false,
    })

    const queryCode = route.query.code as string | undefined

    // Ensure the code doesn't get set from authenticating with MitId
    if (queryCode && !isLoggedIn.value && !route.query.mitid) {
        signupBonusCode.value = queryCode
    }

    const logger = usePromotionLogger().withTag('useSignupBundles')

    const preparedQueryOptions: UseQueryOverrideOptions = {
        lazy: true,
        server: false,
        enabled: () => !isLoggedIn.value,
        ...queryOptions,
    }

    const promotionOfferQuery = useGetSignupOffer(preparedQueryOptions)

    const splittestBundleIds = computed(() => {
        if (!bundlesExperiment.value) return
        if (!signupBundleExperiments) return

        return signupBundleExperiments[bundlesExperiment.value]?.split(',')
    })

    const errorCode = computed(() => promotionOfferQuery.data.value?.error?.errorCode)

    const errorDate = computed(() => {
        const details = promotionOfferQuery.data.value?.error?.details as { startAt?: string, endAt?: string } | undefined
        return details?.endAt
    })

    const activeSignupBundles = computed<PromotionSignupData[] | []>(() => {
        const bundles = promotionOfferQuery.data.value?.promotions

        if (!bundles?.length) return []

        const featuredBundle = bundles.find(bundle => bundle.promotionFields?.featured)

        if (!featuredBundle) {
            if (import.meta.client) {
                logger.error('Featured offer has not been defined, please set the "featured" flag on a bundle in Backoffice. Defaulting to the first active bundle.')
            }

            return [bundles[0]]
        }

        if (signupBundleExperiments) {
            if (bundlesExperiment.value) {
                const siteHasSignupExperiments = Object.values(signupBundleExperiments).every(experiment => experiment)

                if (siteHasSignupExperiments) {
                    const activeSplittestBundles = bundles.reduce((acc: PromotionSignupData[], bundle) => {
                        splittestBundleIds.value?.forEach((bundleId: string | number) => {
                            if (bundle?.id === bundleId.toString()) {
                                acc.push(bundle)

                                return
                            }
                        })

                        return acc
                    }, [])

                    if (activeSplittestBundles?.length) return activeSplittestBundles

                    if (import.meta.client) {
                        logger.error(`No bundles were found matching bundleIds: "${splittestBundleIds.value?.join(',')}" in the splittest for version: "${bundlesExperiment.value}". Defaulting to the default featured signup bundle.`)
                    }

                    return bundles
                }

                if (import.meta.client) {
                    logger.error(`Bundle experiments are enabled by the "${experiment}" cookie, but the config does not have bundle experiments defined for both the "a" and "b" splittest in nuxt.config.ts for the promotion module. Defaulting to the default featured signup bundle.`)
                }

                return bundles
            }

            if (import.meta.client) {
                logger.error(`Bundle experiments are defined, but the function is missing the "${experiment}" cookie. Defaulting to the default featured signup bundle.`)
            }

            return bundles
        }

        return bundles
    })

    const signupBundles = computed<SignupOffer[] | []>(() => {
        if (!activeSignupBundles.value.length) return []

        return activeSignupBundles.value.reduce((acc: SignupOffer[], bundle) => {
            acc.push({
                ...bundle,
                backgroundColor: bundle?.promotionFields?.backgroundColor,
                bundleId: bundle?.id,
                declineable: bundle?.promotionFields?.declinable,
                featured: bundle?.promotionFields?.featured,
                longTerms: bundle?.promotionFields?.fullTerms,
                shortTerms: bundle?.promotionFields?.shortTerms,
                showTermsApplySeeBelow: bundle?.promotionFields?.showShortTermsBelowHero,
                signupFlowImagePath: bundle?.promotionFields?.flowImagePath || '/media/reward/gift.svg',
                signupFlowLabel: bundle?.promotionFields?.flowLabel,
                label: bundle?.promotionFields?.label,
                subtitle: bundle?.promotionFields?.subtitle,
                title: bundle?.promotionFields?.title,
            })

            return acc
        }, [])
    })

    const defaultBundle = computed<SignupOffer | undefined>(() => {
        if (!signupBundles.value?.length) return

        const featuredBundle = signupBundles.value.find(bundle => bundle.featured && bundle.declineable)

        if (!featuredBundle) {
            if (import.meta.client) {
                logger.error('Missing featured offer, please check the configuration for bundles in Backoffice. Defaulting to the first signup bundle.')
            }

            return signupBundles.value.find(bundle => bundle.declineable)
        }

        return featuredBundle
    })

    return promotionOfferQuery.enrich({
        errorCode,
        errorDate,
        signupBundles,
        defaultBundle,
    })
})
